<template>
  <div class="VerticalMenus">
    <MainVerticalMenu
      :items="mainNavbarItems"
      :has-context-menu="contextMenu.items.length > 0"
      :menu-expanded="menuExpanded"
      :pending-request="pendingRequest"
      :role="role"
      @main-menu-expand="(open) => $emit('main-menu-expand', open)"
      @click-on-item="refreshActiveLinkDisplay($router.currentRoute.value)"
      @reset-notif-porteur="resetNotifPorteur()"
    />
    <SubVerticalMenu
      v-show="contextMenu.items.length"
      :items="contextMenu.items"
      :active-anchor="contextMenu.activeAnchor"
      :previous-screen-name="previousScreenName"
      :pending-request="pendingRequest"
      :pending-request-member="pendingRequestMember"
      :role="role"
      :id-grae="graeId"
      :notifications="notifications"
      :menu-expanded="menuExpanded"
      @context-menu-expand="(open) => $emit('context-menu-expand', open)"
      @back-to-previous="backToPreviousRoute()"
      @reset-notif-grae="(event) => resestNotifGraeId(event)"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue"
import { Route } from "vue-router"
import { t } from "@/utils/i18n-utils"
import { UtilisateurDTO } from "@/model/bean/GeneratedDTOs"
import { PROFIL_UTILISATEUR } from "@/model/bean/EnumHelper"
import { UsersService } from "@/services/rest/users/UsersService"
import { ProjectService } from "@/services/rest/project/ProjectService"
import { LoginService } from "@/services/rest/login/LoginService"
import { ContextMenu, NavbarItem } from "@/views/menus/MenuItems"
import MainVerticalMenu from "./MainVerticalMenu.vue"
import SubVerticalMenu from "./SubVerticalMenu.vue"

const MENU_LABELS_WITH_SUB_MENU = {
  STRUCTURE: t("menu.myStructure"),
  ETABLISSEMENT: t("menu.myEtablissement"),
  INSTRUCTION: t("menu.instruction"),
  PERIODS: t("menu.periods"),
  AIRES_EDUCATIVES: t("menu.aires-educatives"),
  GRAE: t("menu.grae"),
  DOCUMENT: t("menu.documents"),
  CARTE: t("menu.carte"),
}

export default defineComponent({
  name: "VerticalMenus",
  components: { MainVerticalMenu, SubVerticalMenu },
  props: {
    loggedUser: {
      type: Object as PropType<UtilisateurDTO>,
      required: false,
      default: () => undefined,
    },
    contextMenuProp: {
      type: Object as PropType<ContextMenu>,
      required: false,
      default: () => new ContextMenu([], []),
    },
    menuExpanded: {
      type: Boolean,
      required: false,
    },
    currentScreenName: {
      type: String,
      required: false,
      default: "",
    },
  },

  emits: ["main-menu-expand", "context-menu-expand", "show-sub-menu"],
  data() {
    return {
      contextMenu: new ContextMenu([], []),
      usersService: UsersService.INSTANCE,
      projectService: ProjectService.INSTANCE,
      isAccompagnateurNationalOrAdmin: false,
      isObservateurNational: false,
      isAccompagnateurRegional: false,
      isReferent: false,
      isEnseignant: false,
      innerLoggedUser: new UtilisateurDTO() as UtilisateurDTO | undefined,
      mainNavbarItems: [] as NavbarItem[],
      previousRoutes: [] as Route[],
      previousScreenName: "",
      previousScreenNames: [] as string[],
      backWasClicked: false,
      storeNextScreenName: true,
      graeId: "" as string | undefined,
      pendingRequest: {
        users: 0,
        modif_porteur: 0,
      },
      pendingRequestMember: {
        users: 0,
        modif_porteur: 0,
      },
      role: "" as string | undefined,
      notifications: {} as { [index: string]: number },
      refreshTimerId: undefined as number | undefined,
    }
  },
  watch: {
    loggedUser(newLoggedUser: UtilisateurDTO): void {
      this.graeId = newLoggedUser?.grae?.id
      this.role = newLoggedUser?.profil
      this.refreshMenuForNewLoggedUser(newLoggedUser)
      this.innerLoggedUser = newLoggedUser
      if (newLoggedUser) {
        void this.handlePendingRequestAndNotification()
        this.startPendingRequestsPolling()
      } else {
        this.stopPendingRequestsPolling()
      }
    },
    currentScreenName(): void {
      if (this.storeNextScreenName) {
        // Initialize first previous screen name if this is the first page seen
        if (this.previousScreenNames.length == 0) {
          this.previousScreenNames.unshift(this.getDefaultPreviousScreenName())
          this.previousScreenName = this.previousScreenNames[0]
        }

        // Then store current screen in the previous screen names list
        this.previousScreenNames.push(this.currentScreenName)
        this.storeNextScreenName = false
      }
    },
    contextMenuProp(newContextMenu: ContextMenu): void {
      this.contextMenu = newContextMenu
      // Use nextTick to avoid recursive updates in RouterLink components
      this.$nextTick(() => {
        this.refreshActiveLinkDisplay(this.$router.currentRoute.value)
      })
      this.$emit("show-sub-menu", newContextMenu.items.length > 0)
    },
  },
  mounted(): void {
    this.innerLoggedUser = this.loggedUser
    // this.graeId = this.loggedUser?.grae?.id
    // Before each route, remember the last visited page to be able to handle back button behavior
    this.$router.beforeEach((to: any, from: any, next: any) => {
      // Empty contextual menu before changing route
      if (from.name?.split(" ")[0] != to.name?.split(" ")[0]) {
        if (!this.backWasClicked) {
          this.previousScreenName = this.currentScreenName
          this.storeNextScreenName = true
          this.previousRoutes.push(from)
          this.contextMenu.items = []
          this.contextMenu.parentsPath = []
          this.refreshActiveLinkDisplay(to)
          this.$emit("show-sub-menu", false)
        } else {
          this.$emit("show-sub-menu", false)
          const previous = this.previousScreenNames[this.previousScreenNames.length - 2]
          this.previousScreenName = previous ? previous : this.getDefaultPreviousScreenName()
        }
        this.backWasClicked = false
      }
      this.refreshActiveLinkDisplay(to)
      next()
    })

    if (this.loggedUser) {
      this.handlePendingRequestAndNotification()
    }
    this.startPendingRequestsPolling()
  },
  beforeUnmount(): void {
    this.stopPendingRequestsPolling()
  },
  methods: {
    startPendingRequestsPolling(): void {
      this.stopPendingRequestsPolling()
      this.refreshTimerId = window.setInterval(() => {
        if (this.loggedUser) {
          void this.handlePendingRequestAndNotification()
        }
      }, 60000) // Raffraichissement toutes les minutes
    },
    stopPendingRequestsPolling(): void {
      if (this.refreshTimerId !== undefined) {
        clearInterval(this.refreshTimerId)
        this.refreshTimerId = undefined
      }
    },
    async handlePendingRequestAndNotification(): Promise<void> {
      if (!this.loggedUser) {
        return
      }
      this.graeId = this.loggedUser?.grae?.id
      try {
        // Extract the graeIdRoute from the URL
        const url = new URL(window.location.href)
        const pathParts = url.pathname.split("/")
        const graeIdRoute = pathParts.includes("grae")
          ? pathParts[pathParts.indexOf("grae") + 3]
          : "all"
        const gareid = graeIdRoute != null ? graeIdRoute : this.graeId
        if (this.role && gareid /*&& graeIdRoute != "all"*/) {
          if (
            this.usersService.hasAnyProfile(this.loggedUser, [
              PROFIL_UTILISATEUR.ANIMATEUR_REGIONAL,
              PROFIL_UTILISATEUR.ACCOMPAGNATEUR_REGIONAL,
            ])
          ) {
            this.pendingRequest.users = await this.usersService.getNumberOfConsentement(gareid)
            this.pendingRequestMember = this.pendingRequest
          }
          if (
            this.usersService.hasAnyProfile(this.loggedUser, [
              PROFIL_UTILISATEUR.ACCOMPAGNATEUR_NATIONAL,
              PROFIL_UTILISATEUR.OBSERVATEUR_NATIONAL,
            ])
          ) {
            this.pendingRequest.users = await this.usersService.getNumberOfConsentementNational()
            this.pendingRequestMember.users =
              await this.usersService.getNumberOfConsentement(gareid)
          }
        }

        const notifs = await this.projectService.getNotificationPP(this.graeId)
        if (notifs) {
          this.pendingRequest.modif_porteur = notifs[0].total
          this.notifications = notifs[0].notifications
        }
      } catch (error) {
        console.error("Error fetching number of consentement:", error)
      }
    },

    /**
     * Send reset notification porteur backend
     */
    async resetNotifPorteur(): Promise<void> {
      this.pendingRequest.modif_porteur = 0
      await this.projectService.resetNotificationPP(undefined)
    },

    async resestNotifGraeId(event: any): Promise<void> {
      // Mise à jour compteur global
      this.pendingRequest.modif_porteur = this.pendingRequest.modif_porteur - event.countNotifGrae
      // Reset notif grae
      await this.projectService.resetNotificationPP(event.idGrae)
    },

    /**
     * Refreshes the active link (in both main menu and contextual menu)
     */
    refreshActiveLinkDisplay(route: Route): void {
      this.mainNavbarItems.forEach((item) => (item.isLinkActive = false))
      this.contextMenu?.items.forEach((item) => (item.isLinkActive = false))

      // Case 1: there is a contextual menu
      // => Highlight sub-item matching route (if any)
      const matchedSubItem = this.contextMenu?.items.filter((subItem) => {
        return this.itemIsMatchingRoute(subItem, route, false)
      })
      if (matchedSubItem?.length) {
        matchedSubItem[0].isLinkActive = true
        // Also highlight parent of this sub item
        const matchedParentItem = this.mainNavbarItems.filter((mainItem) => {
          const matchedParentPath = this.contextMenu.parentsPath.filter((pathCandidate) => {
            return pathCandidate.replace(/[0-9]/g, "") == mainItem.toPath.replace(/[0-9]/g, "")
          })
          return matchedParentPath.length > 0
        })
        if (matchedParentItem.length) {
          matchedParentItem[0].isLinkActive = true
        }
      } else {
        // Case 2: no context menu
        // => Highlight main navbar item matching route (if any)
        const matchedMainItem = this.mainNavbarItems.filter((subItem) => {
          return this.itemIsMatchingRoute(subItem, route, true)
        })
        if (matchedMainItem.length) {
          matchedMainItem[0].isLinkActive = true
        }
      }
    },

    refreshMenuForNewLoggedUser(newLoggedUser: UtilisateurDTO | undefined): void {
      this.innerLoggedUser = newLoggedUser

      if (this.innerLoggedUser) {
        this.isReferent = this.innerLoggedUser.profil === PROFIL_UTILISATEUR.REFERENT
        this.isEnseignant = this.innerLoggedUser.profil === PROFIL_UTILISATEUR.ENSEIGNANT
        this.isAccompagnateurNationalOrAdmin = this.usersService.isAccompagnateurNationalOrAdmin(
          this.innerLoggedUser,
        )
        this.isObservateurNational = this.usersService.isObservateurNational(this.innerLoggedUser)
        this.isAccompagnateurRegional = this.usersService.isAccompagnateurRegional(
          this.innerLoggedUser,
        )
      } else {
        this.isReferent = false
        this.isEnseignant = false
        this.isAccompagnateurNationalOrAdmin = false
        this.isObservateurNational = false
        this.isAccompagnateurRegional = false
      }

      this.initialiseMainMenu()
    },

    initialiseMainMenu(): void {
      const carteNavBarItem = new NavbarItem(
        "/carte",
        MENU_LABELS_WITH_SUB_MENU.CARTE,
        "map-marked-alt",
        false,
      )
      const graeListNavBarItem = new NavbarItem(
        "/grae-list",
        this.$t("menu.grae-list").toString(),
        "university",
        false,
      )

      if (this.isReferent || this.isEnseignant) {
        const specificItems = this.isReferent
          ? new NavbarItem(
              "/structure/details",
              MENU_LABELS_WITH_SUB_MENU.STRUCTURE,
              "building",
              true,
            )
          : new NavbarItem(
              "/etablissement/details",
              MENU_LABELS_WITH_SUB_MENU.ETABLISSEMENT,
              "building",
              true,
            )

        this.mainNavbarItems = [
          new NavbarItem(
            "/mes-actualites",
            this.$t("menu.actualites").toString(),
            "megaphone_vert.svg",
            false,
            true,
          ),
          new NavbarItem("/myProjects", this.$t("menu.myProjects").toString(), "clipboard", false),
          specificItems,
          carteNavBarItem,
        ]
      } else if (
        this.isAccompagnateurRegional &&
        this.innerLoggedUser &&
        this.innerLoggedUser.instructedGraes &&
        this.innerLoggedUser?.instructedGraes?.length !== 0
      ) {
        this.mainNavbarItems = [carteNavBarItem, graeListNavBarItem]
      } else if (this.isAccompagnateurRegional) {
        this.mainNavbarItems = [
          new NavbarItem(
            "/indicateurs",
            this.$t("menu.indicateurs").toString(),
            "chart-pie",
            false,
          ),
          carteNavBarItem,
          new NavbarItem("/grae/details/0", MENU_LABELS_WITH_SUB_MENU.GRAE, "university", true),
          new NavbarItem(
            "/instruction/inscriptions/0/0",
            MENU_LABELS_WITH_SUB_MENU.INSTRUCTION,
            "gavel",
            false,
          ),
          new NavbarItem(
            "/users-list",
            this.$t("menu.contact-porteurs-projet").toString(),
            "users",
            false,
          ),
        ]
      } else if (this.isAccompagnateurNationalOrAdmin || this.isObservateurNational) {
        this.mainNavbarItems = [
          graeListNavBarItem,
          new NavbarItem(
            "/ae/0",
            MENU_LABELS_WITH_SUB_MENU.AIRES_EDUCATIVES,
            "clipboard-list",
            true,
          ),
          new NavbarItem(
            "/ModifProjet",
            this.$t("menu.ModifProjet").toString(),
            "calendar-week",
            false,
          ),
          new NavbarItem("/users-list", this.$t("menu.utilisateurs").toString(), "users", false),
          new NavbarItem(
            "/actualites",
            this.$t("menu.actualites").toString(),
            "megaphone_vert.svg",
            false,
            true,
          ),
          new NavbarItem("/parameters", MENU_LABELS_WITH_SUB_MENU.PERIODS, "calendar-week", true),
          new NavbarItem("/documents", MENU_LABELS_WITH_SUB_MENU.DOCUMENT, "newspaper", false),
          carteNavBarItem,
        ]
      } else {
        this.mainNavbarItems = []
      }
    },

    /**
     * Indicates if the given item is corresponding to the given route (and hence should be highlighted).
     */
    itemIsMatchingRoute(item: NavbarItem, route: Route | undefined, exactPath: boolean): boolean {
      let itemRoutePath = item.toPath
      const uuidRegex =
        /\/[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}/g
      const numericRegex = /\/[0-9]+/g
      if (!exactPath) {
        itemRoutePath = itemRoutePath.replace(uuidRegex, "")
        itemRoutePath = itemRoutePath.replace(numericRegex, "")
      }
      let currentRoutePath = route?.path
      if (!exactPath) {
        currentRoutePath = currentRoutePath?.replace(uuidRegex, "")
        currentRoutePath = currentRoutePath?.replace(numericRegex, "")
      }

      return itemRoutePath == currentRoutePath
    },

    backToPreviousRoute(): void {
      this.backWasClicked = true
      if (this.previousRoutes.length) {
        const previousRoute = this.previousRoutes.pop()
        this.previousScreenNames.pop()
        if (previousRoute) {
          this.$router.replace(previousRoute.path)
          return
        }
      }
      this.$router.push(LoginService.INSTANCE.getPageToRedirectToAfterLogin(this.loggedUser))
    },

    getDefaultPreviousScreenName(): string {
      if (this.loggedUser) {
        if (
          this.usersService.isAccompagnateurNationalOrAdmin(this.loggedUser) ||
          this.usersService.isObservateurNational(this.loggedUser) ||
          (this.usersService.isAccompagnateurRegional(this.loggedUser) &&
            this.loggedUser.instructedGraes &&
            this.loggedUser.instructedGraes.length !== 0)
        ) {
          return this.$t("menu.grae-list").toString()
        } else if (
          this.usersService.hasAnyProfile(this.loggedUser, [
            PROFIL_UTILISATEUR.ACCOMPAGNATEUR_REGIONAL,
            PROFIL_UTILISATEUR.ANIMATEUR_REGIONAL,
          ])
        ) {
          return this.$t("menu.grae").toString()
        }
      }
      return this.$t("menu.myProjects").toString()
    },
  },
})
</script>

<style lang="scss">
@import "@/styles/variables";

.VerticalMenus {
  display: flex;
  background-color: $main-vertical-menu-bg-color;
  overflow: hidden;

  /* Largeur auto basée sur le contenu */
  width: auto;
  height: 100%;
}
</style>
